import React, { createContext, useCallback, useContext, useState } from "react";
import { styled } from "@mui/material/styles";
import { CheckCircle, AlertTriangle, Info, X } from "react-feather";
import { tokens as t } from "./tokens";

const ToastCtx = createContext(() => {});

export const useToast = () => useContext(ToastCtx);

const Stack = styled("div")({
  position: "fixed",
  bottom: 24,
  right: 24,
  zIndex: 1400,
  display: "flex",
  flexDirection: "column",
  gap: 10,
});

const tones = {
  success: { bar: t.color.green, fg: "#4ade80", Icon: CheckCircle },
  error: { bar: t.color.red, fg: "#fca5a5", Icon: AlertTriangle },
  info: { bar: t.color.brand, fg: "#5eead4", Icon: Info },
};

const Item = styled("div", { shouldForwardProp: (p) => p !== "leaving" })(({ leaving }) => ({
  background: t.color.ink,
  color: "#fff",
  padding: "13px 16px 13px 18px",
  borderRadius: 12,
  fontFamily: t.font.body,
  fontWeight: 600,
  fontSize: 13,
  boxShadow: t.shadow.lg,
  display: "flex",
  alignItems: "center",
  gap: 11,
  minWidth: 260,
  maxWidth: 380,
  transition: "all .3s ease",
  opacity: leaving ? 0 : 1,
  transform: leaving ? "translateX(40px)" : "none",
  animation: leaving ? "none" : "adminSlideIn .3s cubic-bezier(.2,.9,.3,1.2)",
  "@keyframes adminSlideIn": {
    from: { opacity: 0, transform: "translateX(40px)" },
    to: { opacity: 1, transform: "none" },
  },
  "& .ic": { flex: "none", display: "grid", placeItems: "center" },
  "& .ic svg": { width: 18, height: 18 },
  "& .close": {
    marginLeft: "auto",
    color: "rgba(255,255,255,.55)",
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
  },
  "& .close svg": { width: 15, height: 15 },
}));

export const ToastProvider = ({ children }) => {
  const [list, setList] = useState([]);

  const remove = useCallback((id) => {
    setList((l) => l.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
    setTimeout(() => setList((l) => l.filter((x) => x.id !== id)), 300);
  }, []);

  const toast = useCallback(
    (msg, type = "success", duration = 2800) => {
      const id = Date.now() + Math.random();
      setList((l) => [...l, { id, msg, type }]);
      if (duration) setTimeout(() => remove(id), duration);
      return id;
    },
    [remove]
  );

  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <Stack>
        {list.map((x) => {
          const tone = tones[x.type] || tones.info;
          const Icon = tone.Icon;
          return (
            <Item key={x.id} leaving={x.leaving} style={{ borderLeft: `3px solid ${tone.bar}` }}>
              <span className="ic" style={{ color: tone.fg }}>
                <Icon />
              </span>
              {x.msg}
              <button className="close" onClick={() => remove(x.id)} aria-label="Хаах">
                <X />
              </button>
            </Item>
          );
        })}
      </Stack>
    </ToastCtx.Provider>
  );
};

export default ToastProvider;
